import React, { useState, useEffect, useCallback } from 'react';
import {
  getAllUsers,
  getUserDetails,
  getUserWatches,
  getUserLocks,
  updateUserDetails,
  resetUserPassword
} from '../adminApi';
import styles from '../styles/UserManagement.module.css';

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [userDetails, setUserDetails] = useState(null);
  const [userWatches, setUserWatches] = useState([]);
  const [userLocks, setUserLocks] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUserData = useCallback(async () => {
    setLoading(true);
    try {
      const [details, watches, locks] = await Promise.all([
        getUserDetails(selectedUser),
        getUserWatches(selectedUser, selectedYear),
        getUserLocks(selectedUser, selectedYear)
      ]);
      setUserDetails(details.data);
      setUserWatches(watches.data);
      setUserLocks(locks.data);
    } catch (err) {
      setError('Failed to fetch user data');
    } finally {
      setLoading(false);
    }
  }, [selectedUser, selectedYear]);

  useEffect(() => {
    if (selectedUser) {
      fetchUserData();
    }
  }, [selectedUser, selectedYear, fetchUserData]);

  const fetchUsers = async () => {
    try {
      const response = await getAllUsers();
      setUsers(response.data);
    } catch (err) {
      setError('Failed to fetch users');
    }
  };

  const handleUpdateField = async (field, value) => {
    try {
      await updateUserDetails(selectedUser, { [field]: value });
      setUserDetails(prev => ({ ...prev, [field]: value }));
    } catch (err) {
      setError(`Failed to update ${field}`);
    }
  };

  const handlePasswordReset = async () => {
    try {
      await resetUserPassword(selectedUser, newPassword);
      setNewPassword('');
      setError('Password reset successful');
    } catch (err) {
      setError('Failed to reset password');
    }
  };

  const renderYearSelector = () => (
    <div className={styles.yearSelector}>
      <label htmlFor="yearSelect">Select Year:</label>
      <select
        id="yearSelect"
        value={selectedYear}
        onChange={(e) => setSelectedYear(Number(e.target.value))}
        className={styles.select}
      >
        {[...Array(5)].map((_, i) => {
          const year = new Date().getFullYear() - i;
          return (
            <option key={`year-${year}`} value={year}>
              {year}
            </option>
          );
        })}
      </select>
    </div>
  );

  const renderWatchesTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>User Watches</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Week</th>
            <th>Game ID</th>
            <th>Pick Type</th>
            <th>Watch Line</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {userWatches.map((watch) => (
            <tr key={`watch-${watch.game_id}-${watch.pick_type}-${watch.created_at}`}>
              <td>{watch.week}</td>
              <td>{watch.game_id}</td>
              <td>{watch.pick_type}</td>
              <td>{watch.watch_line}</td>
              <td>{new Date(watch.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const renderLocksTable = () => (
    <div className={styles.section}>
      <h4 className={styles.sectionTitle}>User Locks</h4>
      <table className={styles.table}>
        <thead>
          <tr>
            <th>Week</th>
            <th>Game ID</th>
            <th>Pick Type</th>
            <th>Lock Line</th>
            <th>Status</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {userLocks.map((lock) => (
            <tr key={`lock-${lock.game_id}-${lock.pick_type}-${lock.created_at}`}>
              <td>{lock.week}</td>
              <td>{lock.game_id}</td>
              <td>{lock.pick_type}</td>
              <td>{lock.lock_line}</td>
              <td>{lock.status}</td>
              <td>{new Date(lock.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className={styles.userManagement}>
      <h3 className={styles.title}>User Management</h3>
      {error && <p className={styles.error}>{error}</p>}

      <div className={styles.userSelector}>
        <label htmlFor="userSelect">Select a user:</label>
        <select
          id="userSelect"
          value={selectedUser || ''}
          onChange={(e) => setSelectedUser(e.target.value)}
          className={styles.select}
        >
          <option value="">Select a user</option>
          {users.map(user => (
            <option key={user.id} value={user.id}>
              {user.username}
            </option>
          ))}
        </select>
      </div>

      {selectedUser && !loading && userDetails && (
        <>
          <div className={styles.userDetailsPanel}>
            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>User Information</h4>
              <div className={styles.field}>
                <label className={styles.fieldLabel}>Active Status</label>
                <input
                  type="checkbox"
                  checked={!userDetails.inactive}
                  onChange={(e) => handleUpdateField('inactive', !e.target.checked)}
                  className={styles.checkbox}
                />
                <span className={styles.checkboxLabel}>
                  {userDetails.inactive ? 'Inactive' : 'Active'}
                </span>
              </div>
              {Object.entries({
                username: 'Username',
                email: 'Email',
                discord_username: 'Discord Username',
                role: 'Role',
                timezone: 'Timezone',
                phone: 'Phone'
              }).map(([field, label]) => (
                <div key={field} className={styles.field}>
                  <label className={styles.fieldLabel}>{label}</label>
                  <input
                    type="text"
                    value={userDetails[field] || ''}
                    onChange={(e) => handleUpdateField(field, e.target.value)}
                    className={styles.input}
                  />
                </div>
              ))}
            </div>

            <div className={styles.section}>
              <h4 className={styles.sectionTitle}>Password Reset</h4>
              <div className={styles.field}>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="New Password"
                  className={styles.input}
                />
                <button
                  onClick={handlePasswordReset}
                  className={styles.button}
                >
                  Reset Password
                </button>
              </div>
            </div>
          </div>

          {renderYearSelector()}
          {userWatches.length > 0 && renderWatchesTable()}
          {userLocks.length > 0 && renderLocksTable()}
        </>
      )}
    </div>
  );
};

export default UserManagement;
